/**
 * The Sounds tab — ENGINE sounds, and only those.
 *
 * playEngine has two kinds of audio and they are not interchangeable:
 *
 *   engine sounds        global slots the snapshot declares by key, assigned
 *                        from here, stored on `playEngineConfig.sounds`
 *   component sounds     an "audio" dependency of one component, assigned in
 *                        Version Settings beside that component's other assets
 *
 * This panel owns the first kind. It used to be a collapsible pinned to the
 * bottom of Version Settings, where it competed for height with the component
 * tree; it is its own rail tab now, in both editors.
 *
 * ── The two editors differ, and the difference is `assetProvider` ────────────
 *
 * Plain playEngine has a Mongo media library: a slot stores a lightweight
 * "@server:<id>" reference and the bytes are hydrated at load time, so the
 * library dropdown (and deleting from it) is offered.
 *
 * The snapshot editor has no library — a snapshot carries its audio inline, and
 * a "@server:" reference written into one has nothing to resolve it and comes
 * back silent. There `hasLibrary` is false: the dropdown is not rendered at all,
 * upload goes through the provider, and the bytes themselves are the source.
 *
 * That is also why this file carries its own sound-delete dialog. Deleting a
 * library sound has to rebind whatever pointed at it, and the rebind for an
 * engine slot is specific to engine slots — it belongs next to them rather than
 * as a branch inside Version Settings' dependency-shaped delete flow.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";

import DialogSoundDelete from "./dialogSoundDelete";
import { getServerUri } from "../modules/uriUtility";
import { sendCommand } from "../modules/playEngineBridge";
import { getAssetMapFromConfig, normalizeBucket } from "../modules/playEngineAssetUtils";
import { dataUrlToBytes, extensionFromDataUrl } from "../modules/zipArchive";
import PlayEngineTabSearch, { matchesTabSearch } from "./playEngineTabSearch";

const CARD_BG = "#474747";

/** The engine's own handle for a sound slot that has been cleared. */
const EMPTY_AUDIO_HANDLE = "audio:empty";

/**
 * Merge live iframe sound state with persisted playEngineConfig.sounds.
 * Config defines which keys exist; live iframe wins for assignment when loaded.
 */
export function mergeEngineSounds(live, config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return live && typeof live === "object" ? { ...live } : {};
  }
  const merged = {};
  for (const [key, configEntry] of Object.entries(config)) {
    const liveEntry = live?.[key];
    if (liveEntry && (liveEntry.sourceRef || liveEntry.source)) {
      merged[key] = liveEntry;
    } else {
      merged[key] = {
        source: configEntry?.source ?? null,
        sourceRef: configEntry?.sourceRef ?? null,
      };
    }
  }
  return merged;
}

/** Sources a slot names, ignoring the engine's placeholder for "nothing here". */
export function engineSoundSourceCandidates(entry) {
  return [entry?.source, entry?.sourceRef].filter(
    (value) => typeof value === "string" && value && value !== EMPTY_AUDIO_HANDLE
  );
}

/** Whether an engine sound slot has an asset behind it, whatever form it takes. */
export function isEngineSoundAssigned(entry) {
  return engineSoundSourceCandidates(entry).length > 0;
}

/**
 * Something an <audio> element can play for an engine sound slot, or null.
 *
 * The slot holds whatever the snapshot it came from stores: a snapshot that
 * embeds its bytes keeps the audio inline, the version editor keeps a library
 * reference, and the pipeline may point at the asset map instead. Only the
 * library case needs a lookup — the media file is served from the same place
 * the game preview reads it from.
 */
export function engineSoundPlaybackSource(entry, { librarySounds = [], assetsMap = {} } = {}) {
  const candidates = engineSoundSourceCandidates(entry);

  const inline = candidates.find((v) => v.startsWith("data:"));
  if (inline) return inline;

  const serverRef = candidates.find((v) => v.startsWith("@server:"));
  if (serverRef) {
    const mongoId = serverRef.slice("@server:".length);
    const record = librarySounds.find((sound) => String(sound._id) === mongoId);
    return record ? `sounds/${record.file}` : null;
  }

  // An asset-map key, bare or as the engine's "audio:<key>" handle id.
  for (const candidate of candidates) {
    const key = candidate.startsWith("audio:") ? candidate.slice("audio:".length) : candidate;
    const mapped = _.get(assetsMap, [key, "source"]);
    if (typeof mapped === "string" && mapped.startsWith("data:")) return mapped;
  }
  return null;
}

/** The extension a playback source implies, mp3 when it says nothing. */
function audioExtension(src) {
  if (typeof src !== "string") return "mp3";
  if (src.startsWith("data:")) return extensionFromDataUrl(src, "mp3");
  const match = /\.([a-z0-9]+)(?:[?#]|$)/i.exec(src);
  return match ? match[1].toLowerCase() : "mp3";
}

/** `audio_button_click.mp3` — what one downloaded slot is called. */
export function soundDownloadName(key, src) {
  const stem = String(key || "sound").replace(/[/\\?%*:|"<>]/g, "-").trim() || "sound";
  return `audio_${stem}.${audioExtension(src)}`;
}

/**
 * Save a slot's audio to disk, straight from what the preview button plays.
 *
 * Inline bytes go through a blob rather than being handed to the anchor as a
 * data URL: a snapshot's embedded audio is long enough that browsers get picky
 * about one. A library sound is a same-origin path, where the anchor is enough.
 */
function downloadSound(key, src) {
  const name = soundDownloadName(key, src);
  const bytes = src.startsWith("data:") ? dataUrlToBytes(src) : null;
  const objectUrl = bytes ? URL.createObjectURL(new Blob([bytes])) : null;

  const link = document.createElement("a");
  link.href = objectUrl || src;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox needs the object to outlive the click; a tick is enough for both.
  if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** A local File as a base64 data-URL. */
function localFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** A library sound, fetched from where the preview reads it, as base64. */
async function soundFileToBase64(filename) {
  const resp = await fetch(`sounds/${filename}`);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** The label a Select shows for the currently bound record. */
function renderSoundSelectValue(value, records) {
  if (!value) return <em style={{ color: "#888" }}>— select asset —</em>;
  const record = records.find((r) => String(r._id) === String(value));
  if (!record) return <em style={{ color: "#888" }}>— select asset —</em>;
  return record.name || record.uploadedFileName || record.file;
}

/** The ✕ inside a dropdown row: deletes that sound from the media library. */
function DropdownRemoveButton({ onClick }) {
  return (
    <IconButton size="small" style={{ padding: "2px", marginLeft: "8px" }} onClick={onClick}>
      <DeleteIcon fontSize="small" style={{ color: "#ef5350" }} />
    </IconButton>
  );
}

DropdownRemoveButton.propTypes = { onClick: PropTypes.func };

export default function ContainerPlayEngineSounds(props) {
  const [engineSounds, setEngineSounds] = useState({});
  const [search, setSearch] = useState("");
  const [busyKeys, setBusyKeys] = useState({});
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null, selectedId: null });
  // What to rebind once the delete goes through, or null.
  const rebindRef = useRef(null);

  const hasAssetLibrary = _.get(props, "assetProvider.hasLibrary", true);
  const providerUploadAsset = _.get(props, "assetProvider.uploadAsset", null);
  const allSounds = _.get(props, "sounds", []) || [];
  const configSounds = _.get(props, "selectedVersion.playEngineConfig.sounds");
  const assetsMap = getAssetMapFromConfig(_.get(props, "selectedVersion.playEngineConfig")) || {};

  const setBusy = (key, value) => setBusyKeys((prev) => ({ ...prev, [key]: value }));

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
  }, []);

  // Nothing should keep playing once this panel is gone.
  useEffect(() => stopPreview, [stopPreview]);

  /**
   * Ask the running engine which slots it has, and merge with what is saved.
   *
   * Reads the saved config through a REF, so this callback keeps its identity.
   * Depending on the config directly cost a round-trip to the iframe on every
   * edit: this panel stays mounted behind the other tabs, and in the snapshot
   * editor the config object is rebuilt whenever the snapshot changes, so every
   * keystroke that touched it fired another postMessage at an engine that was
   * often still loading.
   */
  const configSoundsRef = useRef(configSounds);
  configSoundsRef.current = configSounds;

  const fetchEngineSounds = useCallback(() => {
    if (!props.iFrameId) return;
    const saved = configSoundsRef.current || {};
    sendCommand(props.iFrameId, { type: "playengine:get-engine-sounds" })
      .then((resp) => setEngineSounds(mergeEngineSounds(resp.sounds || {}, saved)))
      .catch((err) => {
        console.warn("[playEngineSounds] fetch failed:", err.message);
        setEngineSounds((prev) => mergeEngineSounds(prev, saved));
      });
  }, [props.iFrameId]);

  // Mount, a new iframe, and the explicit "something rewrote the config"
  // signal — not every edit. What is saved still reaches the panel without a
  // round-trip, through the merge effect below.
  useEffect(() => {
    fetchEngineSounds();
  }, [fetchEngineSounds, props.externalConfigRevision]);

  // Keep in step when the config changes underneath (the agent's
  // add_engine_sound, or an undo) without waiting for an iframe reload.
  useEffect(() => {
    if (!configSounds || typeof configSounds !== "object" || Array.isArray(configSounds)) {
      setEngineSounds({});
      return;
    }
    setEngineSounds((prev) => mergeEngineSounds(prev, configSounds));
  }, [configSounds]);

  /** Persist and live-preview an asset assignment for one engine sound key. */
  async function applyAsset(key, mongoId, base64Data) {
    // With a library the slot stores the lightweight reference and the bytes are
    // hydrated at load time; without one the bytes ARE the source, which is what
    // keeps a snapshot self-contained.
    const sourceRef = hasAssetLibrary && mongoId ? `@server:${mongoId}` : null;
    const source = hasAssetLibrary ? `@server:${mongoId}` : base64Data;

    setBusy(key, true);
    try {
      const sounds = _.cloneDeep(configSounds || {});
      sounds[key] = { source, sourceRef };
      props.setSelectedVersionPathValue({ path: "playEngineConfig.sounds", value: sounds });

      await sendCommand(props.iFrameId, {
        type: "playengine:set-engine-sound-asset",
        key,
        source: base64Data,
        sourceRef,
      });

      setEngineSounds((prev) => ({ ...prev, [key]: { source, sourceRef } }));
    } catch (err) {
      props.createSnackbar?.(`Failed to apply sound "${key}": ${err.message}`, "error", false);
    } finally {
      setBusy(key, false);
    }
  }

  /**
   * Upload an audio file and bind it to a slot.
   *
   * An embedder that brings its own upload (the snapshot editor) takes the
   * shortcut: it hands back the bytes to embed, and nothing reaches the library.
   */
  async function handleUpload(key, file) {
    if (props.userPermissions?.insert === false) {
      props.createSnackbar?.("You are not authorized to upload.", "warning", false);
      return;
    }
    setBusy(key, true);
    try {
      if (providerUploadAsset) {
        const { mongoId = null, base64 } = await providerUploadAsset(file, "audio", "nobucket");
        await applyAsset(key, mongoId, base64);
        return;
      }

      const formData = new FormData();
      formData.append("files", file);
      formData.append("componentType", null);
      formData.append("dependencyKey", key);
      formData.append("bucket", "nobucket");
      formData.append("accessToken", localStorage.getItem("session.accessToken"));
      formData.append("uploadedFileName", file.name.replace(/\.[^/.]+$/, ""));

      const resp = await fetch(getServerUri("sound/uploadSound"), { method: "POST", body: formData });
      if (resp.status !== 200) {
        if (resp.status === 401) props.createSnackbar?.("Not authorized to upload.", "warning", false);
        throw new Error(`Upload failed (status ${resp.status})`);
      }

      const records = _.get(await resp.json(), "records", []);
      if (!records.length) throw new Error("No records returned from upload");

      props.insertSoundPathValue?.(records);
      await applyAsset(key, String(records[0]._id), await localFileToBase64(file));
    } catch (err) {
      console.error("[playEngineSounds] upload failed:", err);
      props.createSnackbar?.(`Upload failed: ${err.message}`, "error", false);
    } finally {
      setBusy(key, false);
    }
  }

  /** Clear a slot back to "never assigned". */
  async function handleClear(key) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    setBusy(key, true);
    try {
      const sounds = _.cloneDeep(configSounds || {});
      sounds[key] = { source: null, sourceRef: null };
      props.setSelectedVersionPathValue({ path: "playEngineConfig.sounds", value: sounds });

      try {
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: {
            target: "engine",
            name: "setEngineSoundAsset",
            args: { key, assetId: EMPTY_AUDIO_HANDLE },
          },
        });
      } catch (err) {
        console.warn(`[playEngineSounds] live-preview dispatch failed: ${err.message}`);
      }

      setEngineSounds((prev) => ({ ...prev, [key]: { source: null, sourceRef: null } }));
      if (playingKey === key) stopPreview();
    } catch (err) {
      console.error("[playEngineSounds] clear failed:", err);
      props.createSnackbar?.(`Failed to clear sound "${key}": ${err.message}`, "error", false);
    } finally {
      setBusy(key, false);
    }
  }

  function togglePreview(key, src) {
    if (playingKey === key) {
      stopPreview();
      return;
    }
    stopPreview();
    if (!src) return;
    const audio = new Audio(src);
    audio.onended = () => setPlayingKey(null);
    audio.onerror = () => setPlayingKey(null);
    audioRef.current = audio;
    setPlayingKey(key);
    audio.play().catch(() => setPlayingKey(null));
  }

  function openLibraryDelete(event, record, boundId, key) {
    event.preventDefault();
    event.stopPropagation();
    if (props.userPermissions?.delete === false) {
      props.createSnackbar?.(
        "You do not have permission to delete, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    rebindRef.current = {
      key,
      bucket: record.bucket,
      deletedId: record._id,
      wasSelected: String(boundId) === String(record._id),
    };
    setDeleteDialog({ open: true, record, selectedId: boundId });
  }

  function closeLibraryDelete() {
    rebindRef.current = null;
    setDeleteDialog({ open: false, record: null, selectedId: null });
  }

  /**
   * The deleted sound was bound to a slot, so the slot needs something else.
   *
   * Another sound from the same bucket if there is one, otherwise the slot is
   * cleared — leaving it pointing at a record that no longer exists would play
   * nothing and show no sign of why.
   */
  async function handleAfterLibraryDelete() {
    const context = rebindRef.current;
    rebindRef.current = null;
    if (!context?.wasSelected) return;

    const bucket = normalizeBucket(context.bucket);
    const next = allSounds.find(
      (sound) =>
        normalizeBucket(sound.bucket) === bucket && String(sound._id) !== String(context.deletedId)
    );

    if (next) {
      await applyAsset(context.key, String(next._id), await soundFileToBase64(next.file));
    } else {
      await handleClear(context.key);
    }
  }

  const keys = Object.keys(engineSounds).filter((key) => matchesTabSearch(key, search));

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />
      <Card style={{ margin: "5px", backgroundColor: CARD_BG }}>
        <CardContent>
          {!keys.length ? (
            // The tab is always in the rail, so it has to say something when the
            // playable declares no slots — an empty card reads as a failure.
            <Typography variant="body2" style={{ color: "#aaa" }}>
              {search.trim() ? "No sound names match this search." : "This playable declares no engine sounds. Slots appear here once the snapshot defines them."}
            </Typography>
          ) : (
            keys.map((key) => {
              const entry = engineSounds[key];
              const rawMongoId = entry.sourceRef ? entry.sourceRef.replace(/^@server:/, "") : "";
              const boundId =
                rawMongoId && allSounds.some((s) => String(s._id) === rawMongoId) ? rawMongoId : "";
              const busy = !!busyKeys[key];
              const inputId = `upload-engine-sound-${key}`;
              const isPlaying = playingKey === key;
              // Whether a sound is set, and whether it can be heard, are read off
              // the slot itself. Deriving them from the media library instead left
              // every slot in a snapshot that embeds its own audio looking empty:
              // no tick, no preview, and a dead remove button over a sound that
              // was very much there.
              const isAssigned = isEngineSoundAssigned(entry);
              const playbackSrc = engineSoundPlaybackSource(entry, {
                librarySounds: allSounds,
                assetsMap,
              });

              return (
                <div key={key} style={{ marginBottom: "12px" }}>
                  <Typography
                    variant="caption"
                    style={{ display: "block", color: "#ccc", marginBottom: "4px" }}
                  >
                    {key}&nbsp;
                    <span style={{ color: "#888", fontStyle: "italic" }}>[audio]</span>
                    {isAssigned && <span style={{ color: "#4caf50", marginLeft: "6px" }}>✓</span>}
                  </Typography>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    {hasAssetLibrary && (
                      <Select
                        style={{ width: "100%" }}
                        value={boundId}
                        displayEmpty
                        disabled={busy}
                        renderValue={(value) => renderSoundSelectValue(value, allSounds)}
                        onChange={(event) => {
                          if (props.userPermissions?.update === false) {
                            props.createSnackbar?.(
                              "You do not have permission to update, contact your administrator.",
                              "warning",
                              false
                            );
                            return;
                          }
                          const selectedId = event.target.value;
                          if (!selectedId) return;
                          const record = allSounds.find((s) => String(s._id) === selectedId);
                          if (!record) return;
                          soundFileToBase64(record.file)
                            .then((base64) => applyAsset(key, selectedId, base64))
                            .catch(console.error);
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>— select asset —</em>
                        </MenuItem>
                        {allSounds.map((sound) => (
                          <MenuItem
                            key={String(sound._id)}
                            value={String(sound._id)}
                            style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                          >
                            <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
                              {sound.name || sound.uploadedFileName || sound.file}
                            </Typography>
                            <DropdownRemoveButton
                              onClick={(event) => openLibraryDelete(event, sound, boundId, key)}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    )}

                    <input
                      id={inputId}
                      type="file"
                      hidden
                      accept=".mp3,.ogg,.wav,.m4a,.aac"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) handleUpload(key, file);
                        event.target.value = "";
                      }}
                    />
                    <IconButton
                      size="small"
                      disabled={busy}
                      style={{ marginLeft: "4px", padding: "4px" }}
                      onClick={() => {
                        if (props.userPermissions?.insert === false) {
                          props.createSnackbar?.("You are not authorized to upload.", "warning", false);
                          return;
                        }
                        document.getElementById(inputId)?.click();
                      }}
                    >
                      {busy ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                    </IconButton>

                    <IconButton
                      size="small"
                      disabled={busy || !isAssigned}
                      title="Remove sound"
                      style={{
                        marginLeft: "2px",
                        padding: "4px",
                        color: isAssigned ? "#ef5350" : "#555",
                      }}
                      onClick={() => handleClear(key)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>

                    {playbackSrc && (
                      <IconButton
                        size="small"
                        title={isPlaying ? "Stop preview" : "Preview sound"}
                        style={{
                          marginLeft: "2px",
                          padding: "4px",
                          color: isPlaying ? "#ef5350" : "#90caf9",
                        }}
                        onClick={() => togglePreview(key, playbackSrc)}
                      >
                        {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                      </IconButton>
                    )}

                    {playbackSrc && (
                      <IconButton
                        size="small"
                        title="Download sound"
                        style={{ marginLeft: "2px", padding: "4px", color: "#90caf9" }}
                        onClick={() => {
                          try {
                            downloadSound(key, playbackSrc);
                          } catch (err) {
                            props.createSnackbar?.(
                              `Failed to download sound "${key}": ${err.message}`,
                              "error",
                              false
                            );
                          }
                        }}
                      >
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Only the sound dialog: the other media kinds belong to dependencies,
          which are Version Settings' business, not this panel's. */}
      <DialogSoundDelete
        isOpen={deleteDialog.open}
        setIsOpen={(value) => {
          if (!value) closeLibraryDelete();
        }}
        createSnackbar={props.createSnackbar}
        soundToDelete={deleteDialog.record}
        selectedSoundID={deleteDialog.selectedId}
        sounds={allSounds}
        setSounds={() => {}}
        selectSound={() => {}}
        deleteSoundFromSounds={props.deleteSoundFromSounds}
        onAfterDelete={handleAfterLibraryDelete}
      />
    </div>
  );
}

ContainerPlayEngineSounds.propTypes = {
  iFrameId: PropTypes.string,
  selectedVersion: PropTypes.object,
  setSelectedVersionPathValue: PropTypes.func,
  sounds: PropTypes.array,
  insertSoundPathValue: PropTypes.func,
  deleteSoundFromSounds: PropTypes.func,
  userPermissions: PropTypes.object,
  createSnackbar: PropTypes.func,
  /** Tells the two editors apart — see the header. */
  assetProvider: PropTypes.object,
  /** Bumped when something rewrites the config underneath (undo, agent). */
  externalConfigRevision: PropTypes.number,
};
